import React, { useState } from 'react';
import classNames from 'classnames';

import NamedLink from '../NamedLink/NamedLink';
import { HELPLINE_DISPLAY, HELPLINE_TEL } from '../../containers/LandingPage/CtSections/ctConfig';
import { trackDeliveryChecker } from '../../analytics/ctTracking';
import {
  CONDITIONS,
  DEFAULT_CONDITION,
  DEFAULT_SIZE,
  DEFAULT_SURFACE,
  SITE_CHECKS,
  SIZES,
  SURFACES,
  conditionIdForListing,
  deliveryProfile,
  sizeIdForListing,
} from './deliveryChecker';

import css from './CtDeliveryChecker.module.css';

const Select = ({ id, label, value, options, onChange }) => (
  <div className={css.field}>
    <label className={css.label} htmlFor={id}>
      {label}
    </label>
    <select id={id} className={css.select} value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * Container Matcher & Delivery Checker.
 *
 * Buyers pick a size, condition and delivery surface and see the container's specs, the
 * truck and clearance it needs, and any site prep. Static guidance only: it never quotes a
 * price (FreightQuotePanel does that on listing pages).
 *
 * @param {Object} props
 * @param {'full'|'compact'} [props.variant] 'compact' starts collapsed, for listing pages
 * @param {Object} [props.listing] preselects size and condition from this listing
 * @param {string} [props.idPrefix] keeps field ids unique if used twice on a page
 * @param {string} [props.className]
 */
const CtDeliveryChecker = props => {
  const { variant = 'full', listing, idPrefix = 'ct-fit', className } = props;
  const isCompact = variant === 'compact';
  const listingSize = listing ? sizeIdForListing(listing) : null;
  const listingCondition = listing ? conditionIdForListing(listing) : null;

  const [open, setOpen] = useState(!isCompact);
  const [size, setSize] = useState(listingSize || DEFAULT_SIZE);
  const [condition, setCondition] = useState(listingCondition || DEFAULT_CONDITION);
  const [surface, setSurface] = useState(DEFAULT_SURFACE);
  const [checked, setChecked] = useState({});

  const profile = deliveryProfile({ size, condition, surface });
  const readyCount = SITE_CHECKS.filter(c => checked[c.id]).length;
  const allReady = readyCount === SITE_CHECKS.length;

  const change = (field, setter) => value => {
    setter(value);
    trackDeliveryChecker({ field, value, variant });
  };
  const toggleCheck = id => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const headingId = `${idPrefix}-heading`;
  const bodyId = `${idPrefix}-body`;

  return (
    <section
      className={classNames(css.root, { [css.compact]: isCompact }, className)}
      aria-labelledby={headingId}
    >
      <div className={css.header}>
        <p className={css.eyebrow}>Container Trader tool</p>
        <h2 id={headingId} className={css.heading}>
          {isCompact
            ? 'Will it fit? Check delivery requirements'
            : 'Container Matcher & Delivery Checker'}
        </h2>
        <p className={css.subheading}>Check specs, truck access and site prep before you order.</p>
        {isCompact ? (
          <button
            type="button"
            className={css.toggle}
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen(o => !o)}
          >
            {open ? 'Hide checker' : 'Check my site'}
          </button>
        ) : null}
      </div>

      {open ? (
        <div id={bodyId} className={css.body}>
          <div className={css.fields}>
            <Select
              id={`${idPrefix}-size`}
              label="Container size"
              value={size}
              options={SIZES}
              onChange={change('size', setSize)}
            />
            <Select
              id={`${idPrefix}-condition`}
              label="Condition"
              value={condition}
              options={CONDITIONS}
              onChange={change('condition', setCondition)}
            />
            <div className={css.fieldWide}>
              <Select
                id={`${idPrefix}-surface`}
                label="Where it will sit"
                value={surface}
                options={SURFACES}
                onChange={change('surface', setSurface)}
              />
            </div>
          </div>

          <div className={css.results} aria-live="polite">
            <h3 className={css.resultsHeading}>{profile.size.label}: delivery & spec profile</h3>
            <dl className={css.specs}>
              <div>
                <dt>Exterior</dt>
                <dd>{profile.size.exterior}</dd>
              </div>
              <div>
                <dt>Capacity</dt>
                <dd>{profile.size.capacity}</dd>
              </div>
              <div>
                <dt>Weight</dt>
                <dd>{profile.size.tare}</dd>
              </div>
              <div>
                <dt>Truck</dt>
                <dd>{profile.size.truck}</dd>
              </div>
              <div>
                <dt>Straight run</dt>
                <dd>
                  <strong>{profile.size.straightRun}</strong>
                  <span className={css.ddNote}>{profile.size.straightRunNote}</span>
                </dd>
              </div>
              <div>
                <dt>Path width</dt>
                <dd>{profile.pathWidth}</dd>
              </div>
              <div>
                <dt>Overhead</dt>
                <dd>
                  <strong>{profile.overheadAtDrop}</strong>
                  <span className={css.ddNote}>{profile.overheadOnRoute}</span>
                </dd>
              </div>
            </dl>

            <div
              className={classNames(css.callout, {
                [css.calloutWarn]: profile.surface.status === 'warn',
              })}
            >
              <p className={css.calloutTitle}>
                <span aria-hidden="true">{profile.surface.status === 'warn' ? '!' : '✓'}</span>
                {profile.surface.title}
              </p>
              <p className={css.calloutText}>{profile.surface.note}</p>
            </div>

            <div className={css.callout}>
              <p className={css.calloutTitle}>
                {profile.condition.label}: best for {profile.condition.bestFor.toLowerCase()}
              </p>
              <p className={css.calloutText}>{profile.condition.note}</p>
            </div>
          </div>

          <fieldset className={css.checklist}>
            <legend className={css.checklistLegend}>
              Site checklist{' '}
              <span className={classNames(css.count, { [css.countReady]: allReady })}>
                {readyCount} of {SITE_CHECKS.length} ready
              </span>
            </legend>
            {SITE_CHECKS.map(c => (
              <label key={c.id} className={css.checkItem}>
                <input
                  type="checkbox"
                  checked={!!checked[c.id]}
                  onChange={() => toggleCheck(c.id)}
                />
                <span>{c.label}</span>
              </label>
            ))}
            {allReady ? (
              <p className={css.readyNote}>Your site looks ready for a tilt-bed delivery.</p>
            ) : null}
          </fieldset>

          <p className={css.disclaimer}>
            These are planning figures for tilt-bed delivery. Every site and truck is different, so
            the driver or dispatcher confirms the exact requirements when delivery is booked. A
            20&apos; usually comes on a rollback, but when one is not available it may arrive on a
            40&apos; or longer trailer, so leave room for the longer run if you can. Tight site? Ask
            about other options, such as a flatbed drop when you have a forklift on site.
          </p>

          <div className={css.actions}>
            {isCompact ? (
              <NamedLink name="DeliveryChecklistPage" className={css.secondaryLink}>
                See the full delivery checklist
              </NamedLink>
            ) : (
              <>
                <NamedLink
                  name="SearchPage"
                  to={{ search: `?${profile.size.search}` }}
                  className={css.primaryBtn}
                >
                  Browse {profile.size.label} containers
                </NamedLink>
                <NamedLink name="NearbyPage" className={css.secondaryBtn}>
                  Find containers near my ZIP
                </NamedLink>
              </>
            )}
          </div>
          <p className={css.helpline}>
            Questions about your site? Call <a href={`tel:${HELPLINE_TEL}`}>{HELPLINE_DISPLAY}</a>.
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default CtDeliveryChecker;
